import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useMessageFeedback } from "@/lib/hooks/useFeedback";
import { cn } from "@/lib/utils";
import { useMessageStore } from "@/state/stores";
import { Copy, ThumbsDown, ThumbsUp } from "lucide-react";
import { useCallback } from "react";
import styles from "./ResponseFeedback.module.css";

export function ResponseFeedback({
  messageId,
  projectId,
}: { messageId: string; projectId: string }) {
  const { toast } = useToast();
  const { data: existingFeedback, reportFeedback } = useMessageFeedback(
    projectId,
    messageId,
  );

  const feedbackGiven = existingFeedback
    ? existingFeedback.score === 1
      ? "up"
      : "down"
    : "none";

  const message = useMessageStore((state) =>
    state.messages.find((msg) => msg.id === messageId),
  );

  const extractCopyableContent = useCallback(() => {
    if (!message) {
      return "";
    }

    // For parts-based messages
    if (message.usesParts && message.parts) {
      return message.parts
        .filter((part) => part.type === "text")
        .map((part) => part.content)
        .join("\n");
    }

    // For legacy messages
    return message.content || "";
  }, [message]);

  const handleCopy = useCallback(async () => {
    const content = extractCopyableContent();
    if (!content) {
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      toast({
        duration: 2000,
        title: "Copied!",
        description: "Message content copied to clipboard",
      });
    } catch (error) {
      console.error("Failed to copy content:", error);
      toast({
        duration: 2000,
        title: "Copy failed",
        description: "Unable to copy content to clipboard",
        variant: "destructive",
      });
    }
  }, [extractCopyableContent, toast]);

  const handleThumbsUp = useCallback(async () => {
    await reportFeedback(1);
  }, [reportFeedback]);

  const handleThumbsDown = useCallback(async () => {
    await reportFeedback(0);
  }, [reportFeedback]);

  return (
    <ul className={cn("flex gap-2", styles.feedback)}>
      <li>
        <Button variant="ghost" size="icon-xs" onClick={handleCopy}>
          <Copy />
        </Button>
      </li>
      <li>
        <Button
          variant="ghost"
          size="icon-xs"
          disabled={feedbackGiven !== "none"}
          onClick={handleThumbsUp}
        >
          <ThumbsUp
            className={cn(feedbackGiven === "up" && "text-green-600")}
          />
        </Button>
      </li>
      <li>
        <Button
          variant="ghost"
          size="icon-xs"
          disabled={feedbackGiven !== "none"}
          onClick={handleThumbsDown}
        >
          <ThumbsDown
            className={cn(feedbackGiven === "down" && "text-red-600")}
          />
        </Button>
      </li>
    </ul>
  );
}
